import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { ToastProgrammatic as Toast } from 'buefy';
import isBoolean from 'lodash/isBoolean';
import _cloneDeep from 'lodash/cloneDeep';
import {
  isArray,
} from 'lodash';
import useVuelidate from '@vuelidate/core';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { V1Scheduler } from 'takeorder-groupbuying-scheduler-js';
import { helpers, maxValue, minValue } from '@vuelidate/validators';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import {
  required,
  requiredIf,
  integer,
} from '@/utils/validators';
import {
  ALLOW_EDIT_TYPES,
  CONSUMPTION_LIMIT,
  ENTREE_LIMIT,
  ON_SITE_ORDER_ITEM_INDEX,
  ORDER_STATUS_WITH_TYPE_TEXT,
} from '@/utils/constants';
import weekdayTransformer from '@/utils/weekdayTransformer';
import { UPDATE_DRAFT_ORDER } from '@/store/orders/constants';
import {
  GET_MENU_ITEM_LIST,
  GET_PAYMENT_METHOD_MAPPING,
  GET_PAYMENT_TIMING_MAPPING,
  GET_ORDER_METHOD_MAPPING,
} from '@/store/store/constants';
import {
  GET_GROUP_BUYINGS_MENU,
  GET_GROUP_BUYINGS_MENU_LIMITS,
} from '@/store/menu/constants';
import ScheduleModal from '@/components/Order/ScheduleModal/ScheduleModal.vue';
import ScheduleList from '@/components/Order/OrderCreate/ScheduleList/ScheduleList.vue';
import { toClientSchedule, toServerSchedule } from '@/utils/cycleOrder';

dayjs.locale('zh-tw');

export default {
  name: 'OrderDetail',
  components: {
    ScheduleList,
    ScheduleModal,
    quillEditor,
  },
  props: {
    allowEditType: {
      default: ALLOW_EDIT_TYPES.ALL,
      type: Number,
      validator: (val) => Object.values(ALLOW_EDIT_TYPES).includes(val),
    },
    menu: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    reselectStore: {
      default: false,
      type: Boolean,
    },
    title: {
      type: String,
      required: true,
      default: '',
    },
    viewMode: {
      default: false,
      type: Boolean,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      dividerHide: false,
      enableSchedule: false,
      isOrderPeriodEndSameOrBeforeReceiveTimeStart: true,
      limitAmount: null,
      limitItems: null,
      loading: false,
      menuItems: [],
      menuItemsLimits: {},
      openScheduleModal: false,
      orderInfo: {},
      schedule: {},
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link'],
          ],
        },
        placeholder: '備註將會顯示在前台訂購資訊',
      },
      sendEmailTypes: [
        {
          label: '寄送品項統計表',
          switch: 'enableSendItemStats',
          email: 'itemStatsReceiver',
          tooltip: '1. 開放訂購後寄送即時訂購統計連結 2. 截止訂購後寄送品項統計報表',
        },
        {
          label: '寄送人員訂購列表',
          switch: 'enableSendMemberOrders',
          email: 'memberOrdersReceiver',
          tooltip: '截止訂購後寄送',
        },
        {
          label: '寄送訂購核銷列表',
          switch: 'enableSendOrderRedeems',
          email: 'orderRedeemsReceiver',
          tooltip: '1. 截止訂購後寄送 2. 領取時間結束30分鐘後',
        },
      ],
    };
  },
  watch: {
    limitAmount(newVal, oldVal) {
      const isLimitAmount = this.orderInfo.limit === CONSUMPTION_LIMIT.AMOUNT;
      if (newVal !== oldVal && isLimitAmount) {
        this.tryAndUpdateDraftOrder();
      }
    },
    limitItems(newVal, oldVal) {
      const isLimitItems = this.orderInfo.limit === CONSUMPTION_LIMIT.ITEMS;
      if (newVal !== oldVal && isLimitItems) {
        this.tryAndUpdateDraftOrder();
      }
    },
    orderInfo: {
      handler() {
        this.tryAndUpdateDraftOrder();
      },
      deep: true,
    },
    menuItems: {
      handler() {
        this.tryAndUpdateDraftOrder();
      },
      deep: true,
    },
    receiveDateStart: {
      handler(val) {
        if (!isEmpty(this.schedule)) {
          this.schedule.receiveDateStart = val ? dayjs(val).format() : val;
        }
        if (!isEmpty(this.orderInfo.schedule)) {
          this.orderInfo.schedule.receiveDateStart = val ? dayjs(val).format() : val;
        }
      },
    },
    'schedule.orderStart': {
      handler() {
        this.handleOrderPeriodTimeValidation();
      },
    },
    sortedReceiveTimes: {
      deep: true,
      handler() {
        this.handleOrderPeriodTimeValidation();
      },
    },
    updateOrderFail(newVal) {
      if (newVal) {
        if (this.inEndedOrOnGoing) {
          const { id: menuId, storeInfo: { id: storeId } } = this.menu;
          this.loadMenuLimits(storeId, menuId);
        }
      }
    },
  },
  computed: {
    ...mapGetters('orders', ['draftBuyingMethod']),
    ...mapGetters('store', {
      paymentMethods: GET_PAYMENT_METHOD_MAPPING,
      paymentTimings: GET_PAYMENT_TIMING_MAPPING,
      orderMethods: GET_ORDER_METHOD_MAPPING,
    }),
    ...mapState('orders', ['draftOrder', 'updateOrderFail']),
    consumptionLimitAmount() {
      switch (this.orderInfo.limit) {
        case CONSUMPTION_LIMIT.AMOUNT:
          return this.limitAmount || 0;

        case CONSUMPTION_LIMIT.ITEMS:
          return this.limitItems || 0;

        default:
          return 0;
      }
    },
    consumptionLimitList() {
      return { ...CONSUMPTION_LIMIT };
    },
    endTimeErrorMsg() {
      if (this.viewMode) {
        return '';
      }
      const isTimeBeforeNow = this.isTimeBeforeNow(this.orderInfo.endTime);
      if (isTimeBeforeNow && !this.inEndedOrOnGoing) {
        return '結束時間不得小於現在時間';
      }
      return this.v$.orderInfo.endTime.$dirty
      && this.v$.orderInfo.endTime.$silentErrors.length
        ? this.v$.orderInfo.endTime.$silentErrors[0].$message : '';
    },
    hasSchedule() {
      return !isEmpty(this.schedule);
    },
    hasEmailFills() {
      const HAS_ORDER_INFO = this.v$?.orderInfo;
      const ITEM_STATS_RECEIVER = HAS_ORDER_INFO?.itemStatsReceiver;
      const MEMBER_ORDERS_RECEIVER = HAS_ORDER_INFO?.memberOrdersReceiver;
      const ORDER_REDEEMS_RECEIVER = HAS_ORDER_INFO?.orderRedeemsReceiver;
      return {
        itemStatsReceiver: ITEM_STATS_RECEIVER?.$dirty
          && ITEM_STATS_RECEIVER?.$errors.length,
        memberOrdersReceiver: MEMBER_ORDERS_RECEIVER?.$dirty
          && MEMBER_ORDERS_RECEIVER?.$errors.length,
        orderRedeemsReceiver: ORDER_REDEEMS_RECEIVER?.$dirty
          && ORDER_REDEEMS_RECEIVER?.$errors.length,
      };
    },
    inEndedOrOnGoing() {
      const methods = [
        ORDER_STATUS_WITH_TYPE_TEXT.ENDED,
        ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      ];
      return methods.includes(this.draftBuyingMethod);
    },
    isAllowToEdit() {
      return this.allowEditType === ALLOW_EDIT_TYPES.ALL;
    },
    isReceiveTimeAfterScheduleReceiveTime() {
      const { receiveDateEnd } = this.schedule;
      if (!receiveDateEnd || !this.orderReceiveTimes.length) {
        return false;
      }

      const last = dayjs(receiveDateEnd);
      const times = [...this.orderReceiveTimes];
      return times.some(({ startTime }) => dayjs(startTime).isAfter(last, 'day'));
    },
    notifyDeadlineOptions() {
      return [
        {
          label: '30分鐘',
          value: 30,
        },
        {
          label: '1小時',
          value: 60,
        },
        {
          label: '2小時',
          value: 60 * 2,
        },
        {
          label: '4小時',
          value: 60 * 4,
        },
        {
          label: '8小時',
          value: 60 * 8,
        },
        {
          label: '1天',
          value: 60 * 24,
        },
        {
          label: '3天',
          value: 60 * 24 * 3,
        },
      ];
    },
    receiveDateStart() {
      const { startTime } = this.sortedReceiveTimes?.[0] || {};
      if (!startTime) {
        return null;
      }

      const start = dayjs(startTime);
      if (start.isAfter(dayjs(), 'day')) {
        return start.toDate();
      }

      return dayjs()
        .set('hour', start.hour())
        .set('minute', start.minute())
        .toDate();
    },
    schedules() {
      if (isEmpty(this.schedule)) {
        return [];
      }
      const {
        type,
        dayInterval,
        weekdayIntervals = [],
        orderStart,
        orderPeriodDay,
        orderPeriodTime = {},
        orderCreateDay,
        orderCreateTime,
        receiveDateEnd,
      } = this.schedule;
      const config = {
        type,
        period: dayInterval,
        weekday: [...weekdayIntervals],
        orderStart,
        orderPeriodDay,
        orderPeriodTime: [
          dayjs(orderPeriodTime.start).format('HH:mm'),
          dayjs(orderPeriodTime.end).format('HH:mm'),
        ],
        orderCreateDay,
        orderCreateTime: dayjs(orderCreateTime).format('HH:mm'),
        lastReceiveDate: dayjs(receiveDateEnd).format('YYYY-MM-DD'),
        groupBuyingStartTime: dayjs(this.receiveDateStart || dayjs()).format(),
        groupBuyingReceiveTimes: this.sortedReceiveTimes.map(
          ({ startTime, endTime }) => [dayjs(startTime).format(), dayjs(endTime).format()],
        ),
      };
      const schedules = V1Scheduler.getAllSchedule(config);
      return schedules;
    },
    sortedReceiveTimes() {
      const copy = [...this.orderReceiveTimes];
      copy.sort((a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix());
      return copy;
    },
    storePhone() {
      const orderMethod = this.menu.storeInfo?.store_order_way || {};
      return orderMethod.phone || orderMethod.telephone || '';
    },
    storeClosedDays() {
      const closedDays = this.menu.storeInfo?.store_close_days?.map((d) => d.day) || [];
      return weekdayTransformer(closedDays);
    },
    storePaymentMethods() {
      const storePaymentInfo = Object.keys(this.menu.storeInfo?.store_payment_way || {})
        .filter((key) => this.menu.storeInfo?.store_payment_way[key]);
      const availablePaymentMethods = this.paymentMethods
        .filter((i) => storePaymentInfo.some((m) => i.key === m));
      return availablePaymentMethods.map((m) => m.title).join('、');
    },
    storeOrderMethods() {
      const excludedMethods = ['phone', 'telephone'];
      const orderMethod = this.menu.storeInfo?.store_order_way || {};
      const availableOrderMethods = this.orderMethods
        .filter((i) => !excludedMethods.includes(i.key) && !!orderMethod[i.key]);
      return availableOrderMethods;
    },
    orderTimeRange() {
      return {
        start: {
          min: dayjs().toDate(),
          max: null,
        },
        end: {
          min: this.orderInfo.startTime || dayjs().toDate(),
          max: null,
        },
      };
    },
    orderReceiveTimeRange() {
      return this.orderReceiveTimes.map((t) => {
        const dateToday = dayjs().toDate();
        return {
          start: {
            min: this.inEndedOrOnGoing
              ? dateToday : this.orderInfo.startTime
              || this.orderTimeRange.start.min,
            max: null,
          },
          end: {
            min: this.inEndedOrOnGoing ? dateToday : t?.startTime || dateToday,
            max: null,
          },
        };
      });
    },
    orderReceiveTimes() {
      return this.orderInfo?.teamGroupBuyingReceiveTimes || [];
    },
    finalOrderInfo() {
      const orderInfo = _cloneDeep(this.orderInfo);
      const schedule = toServerSchedule(orderInfo.schedule);

      return {
        ...orderInfo,
        ...{
          endTime: dayjs(orderInfo.endTime).format(),
          limitAmount: this.consumptionLimitAmount,
          startTime: dayjs(orderInfo.startTime).format(),
          teamGroupBuyingReceiveTimes: orderInfo.teamGroupBuyingReceiveTimes?.map(({
            endTime: end,
            startTime: start,
            ...props
          }) => ({
            ...props,
            startTime: dayjs(start).format(),
            endTime: dayjs(end).format(),
          })),
          entreeLimits: this.menuItems
            .filter(({ limit }) => +limit >= 0 && limit !== '' && limit !== null)
            .map(({ index, limit }) => ({ entree_index: index, limit: +limit })),
          schedule,
        },
      };
    },
    minEntreeLimit() {
      return 0;
    },
    maxEntreeLimit() {
      return ENTREE_LIMIT.MAXIMUM;
    },
  },
  async mounted() {
    const { id: menuId, storeInfo: { id: storeId } } = this.menu;

    window.scrollTo({ top: 0 });
    window.addEventListener('resize', this.onWindowResize);
    if (this.inEndedOrOnGoing) {
      this.loadMenuLimits(storeId, menuId);
    }
    await this.loadMenuItems(storeId, menuId);
    this.orderInfo = this.initializeOrderInfo();
  },
  async unmounted() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    ...mapActions('store', {
      getMenuItem: GET_MENU_ITEM_LIST,
    }),
    ...mapActions('menu', {
      getGroupBuyingsMenu: GET_GROUP_BUYINGS_MENU,
      getGroupBuyingsMenuLimits: GET_GROUP_BUYINGS_MENU_LIMITS,
    }),
    ...mapMutations('orders', {
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    beforeAddEmail(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tag);
    },
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    disabledTrash({ endTime: end = '', id, startTime: start = '' }) {
      const now = dayjs();
      return (id && this.inEndedOrOnGoing)
        || (!this.isAllowToEdit && (dayjs(end).isBefore(now) || dayjs(start).isBefore(now)));
    },
    disabledTime(time = '') {
      const now = dayjs();
      return !this.isAllowToEdit && dayjs(time).isBefore(now, 'm');
    },
    filterNonPositiveNumber($event) {
      const keyCode = $event.keyCode || $event.which;
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },
    closeTags({ email }, index, event) {
      if (this.$refs[`${email}_taginput`]) {
        this.$refs[`${email}_taginput`][0].removeTag(index, event);
      }
    },
    getDefaultEmails() {
      const { storeInfo } = this.menu || {};
      const { emails, store_detail: storeDetail } = storeInfo || {};
      return this.inEndedOrOnGoing
        ? (emails || [])
        : (storeDetail?.emails || []);
    },
    onSendEmailSwitchChange({ switch: switchKey, email: emailField }) {
      const isEnabled = this.orderInfo[switchKey];
      const currentEmails = this.orderInfo[emailField] || [];
      if (isEnabled && currentEmails.length === 0) {
        this.orderInfo[emailField] = [...this.getDefaultEmails()];
      }
    },
    initializeOrderInfo() {
      const { storeInfo, name: menuName = '' } = this.menu || {};
      const { store_detail: storeDetail } = storeInfo || {};
      const storeName = storeDetail?.name || '';
      const now = new Date();

      const {
        enableOrderNote,
        enableSchedule: defaultEnableSchedule,
        enableSendEmail,
        endTime,
        itemStatsReceiver,
        memberOrdersReceiver,
        name: draftName,
        orderRedeemsReceiver,
        schedule: defaultSchedule,
        startTime = now,
        teamGroupBuyingReceiveTimes: draftReceiveTimes = [],
        subsidy = 0,
      } = this.draftOrder;
      const defaultReceiveTime = [
        {
          startTime: dayjs(endTime || now).add(2, 'hour').toDate(),
          endTime: dayjs(endTime || now).add(3, 'hour').toDate(),
        },
      ];
      const receiveTime = draftReceiveTimes?.length
        ? draftReceiveTimes.map(({ startTime: start, endTime: end, ...props }) => ({
          endTime: dayjs(end).toDate(),
          startTime: dayjs(start).toDate(),
          ...props,
        })) : defaultReceiveTime;

      const enableSchedule = isNil(defaultEnableSchedule)
        ? !!defaultSchedule : defaultEnableSchedule;
      const schedule = toClientSchedule(defaultSchedule);

      const lastEndTime = endTime ? dayjs(endTime).toDate() : dayjs(now).add(2, 'hour').toDate();
      const orderInfo = {
        ...this.draftOrder,
        ...{
          enableSchedule,
          endTime: lastEndTime,
          itemStatsReceiver,
          memberOrdersReceiver,
          name: draftName || `${storeName}/${menuName}`,
          orderRedeemsReceiver,
          startTime: dayjs(startTime || now).toDate(),
          teamGroupBuyingReceiveTimes: receiveTime,
          enableOrderNote: !!enableOrderNote,
          enableSendEmail: isBoolean(enableSendEmail) ? enableSendEmail : false,
          schedule,
          subsidy,
        },
      };

      if (isNil(orderInfo.limit)) {
        orderInfo.limit = CONSUMPTION_LIMIT.NO_LIMIT;
      }
      this.setConsumptionLimitAmount(orderInfo.limit, orderInfo.limitAmount);

      this.enableSchedule = enableSchedule;
      this.schedule = schedule;

      if (this.enableSchedule) {
        this.v$.enableSchedule.$touch();
      }

      return orderInfo;
    },
    getMenuData(storeId, menuId) {
      const { params: { orderId } = {} } = this.$route || {};
      if (orderId && !this.reselectStore) {
        return this.getGroupBuyingsMenu(orderId);
      }
      return this.getMenuItem({ storeId, menuId });
    },
    async loadMenuLimits() {
      try {
        this.loading = true;
        const { params: { orderId } = {} } = this.$route || {};
        this.menuItemsLimits = await this.getGroupBuyingsMenuLimits(orderId);
      } catch (ex) {
        Toast.open({
          type: 'is-danger',
          message: ex,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadMenuItems(storeId, menuId) {
      try {
        this.loading = true;
        const { categories = [], entree = [] } = await this.getMenuData(storeId, menuId);
        const { entreeLimits: originEntreeData = {} } = this.draftOrder || {};
        const entreeLimits = {};
        if (isArray(originEntreeData)) {
          originEntreeData.forEach(({ entree_index: index, limit }) => {
            entreeLimits[index] = { limit };
          });
        } else {
          Object.assign(entreeLimits, originEntreeData);
        }
        this.menuItems = _cloneDeep(entree)
          ?.filter(({ index }) => index !== ON_SITE_ORDER_ITEM_INDEX)
          ?.sort((a, b) => a.category_index - b.category_index
          || a.view_index - b.view_index)
          ?.map((item) => {
            const entreeLimit = entreeLimits[item.index]?.limit === 0
              ? 0
              : entreeLimits[item.index]?.limit || null;
            return {
              ...item,
              category: categories.find(({ index }) => index === item?.category_index)?.name || '',
              limit: isNil(entreeLimit) ? null : +(entreeLimit),
            };
          }) || [];
      } catch (ex) {
        Toast.open({
          type: 'is-danger',
          message: '載入菜單時發生錯誤，請稍後再試',
        });
      } finally {
        this.loading = false;
      }
    },
    getOrderMethodImage(filename) {
      const images = require.context('@/assets/images', false, /\.svg$/);
      return images(`./${filename}`);
    },
    datetimeFormatter(time) {
      return dayjs(time).format('YYYY/MM/DD a hh:mm');
    },
    handleOrderPeriodTimeValidation() {
      if (this.enableSchedule && !isEmpty(this.schedule)) {
        const { startTime: start } = (this.sortedReceiveTimes || [])[0] || {};
        const { orderPeriodTime: { end } = {}, orderStart = 0 } = this.schedule;
        if (start && end && !orderStart) {
          const orderPeriodTimeEnd = dayjs(end);
          const offsetEnd = dayjs(start)
            .hour(orderPeriodTimeEnd.hour())
            .minute(orderPeriodTimeEnd.minute());
          this.isOrderPeriodEndSameOrBeforeReceiveTimeStart = !dayjs(offsetEnd)
            .isAfter(dayjs(start), 'minute');
          return;
        }
      }
      this.isOrderPeriodEndSameOrBeforeReceiveTimeStart = true;
    },
    isTimeBeforeNow(time) {
      const now = dayjs();
      return dayjs(time).isBefore(now);
    },
    onEnableScheduleChange(val) {
      this.v$.enableSchedule.$touch();

      this.orderInfo.enableSchedule = val;
      this.orderInfo.schedule = this.schedule;
      if (!val) {
        this.orderInfo.schedule.departmentIds = this.orderInfo?.departmentIds || [];
      }
      this.tryAndUpdateDraftOrder();
    },
    onScheduleChange(val) {
      this.v$.enableSchedule.$touch();

      this.schedule = val;
      this.orderInfo.schedule = val;
      this.tryAndUpdateDraftOrder();
    },
    onStartTimeChange() {
      this.v$.orderInfo.startTime.$touch();
      if (dayjs(this.orderInfo.endTime).isBefore(this.orderInfo.startTime)) {
        this.orderInfo.endTime = dayjs(this.orderInfo.startTime).add(2, 'hour').toDate();
      }
      this.orderReceiveTimes.forEach((time, i) => {
        if (dayjs(time.startTime).isBefore(this.orderInfo.startTime)) {
          this.orderReceiveTimes[i].startTime = dayjs(this.orderInfo.startTime).add(2, 'hour').toDate();
        }
        if (dayjs(time.endTime).isBefore(time.startTime)) {
          this.orderReceiveTimes[i].endTime = dayjs(time.startTime).add(2, 'hour').toDate();
        }
      });
    },
    onEndTimeChange() {
      this.v$.orderInfo.endTime.$touch();
      if (dayjs(this.orderInfo.startTime).isAfter(this.orderInfo.endTime)) {
        this.orderInfo.startTime = dayjs(this.orderInfo.endTime).subtract(2, 'hour').toDate();
      }
    },
    onReceiveStartTimeChange(index) {
      this.v$.enableSchedule.$touch();

      const target = this.orderReceiveTimes[index];

      if (dayjs(target.endTime).isBefore(target.startTime)) {
        target.endTime = dayjs(target.startTime).add(2, 'hour').toDate();
      }
    },
    onReceiveEndTimeChange(index) {
      this.v$.enableSchedule.$touch();

      const target = this.orderReceiveTimes[index];

      if (dayjs(target.startTime).isAfter(target.endTime)) {
        target.startTime = dayjs(target.endTime).subtract(2, 'hour').toDate();
      }
    },
    addReceiveTime() {
      this.v$.enableSchedule.$touch();

      const lastReceiveTime = this.orderReceiveTimes[this.orderReceiveTimes.length - 1];
      const endTime = this.inEndedOrOnGoing ? dayjs() : lastReceiveTime?.endTime || '';
      const newReceiveTime = {
        startTime: dayjs(endTime).add(2, 'hour').toDate(),
        endTime: dayjs(endTime).add(3, 'hour').toDate(),
      };
      this.orderInfo.teamGroupBuyingReceiveTimes.push(newReceiveTime);
    },
    removeReceiveTime(index) {
      this.orderInfo.teamGroupBuyingReceiveTimes.splice(index, 1);
    },
    prevStep() {
      this.$emit('prev-step');
    },
    setConsumptionLimitAmount(limit = this.orderInfo.limit, amount = 0) {
      switch (limit) {
        case CONSUMPTION_LIMIT.AMOUNT:
          this.limitAmount = amount;
          break;

        case CONSUMPTION_LIMIT.ITEMS:
          this.limitItems = amount;
          break;

        default:
          break;
      }
    },
    showLimitOption(val) {
      return !this.viewMode || (this.viewMode && this.orderInfo.limit === val);
    },
    focusOnLimitInput(target) {
      const limitInput = this.$refs[`limitInput${target}`];
      if (!limitInput) {
        return;
      }
      limitInput.focus();
    },
    tryAndUpdateDraftOrder() {
      if (isEmpty(this.orderInfo)) {
        return;
      }
      this.updateDraftOrder(this.finalOrderInfo);
    },
    onWindowResize() {
      const { width } = this.$refs.flexBox?.getBoundingClientRect() || {};
      if (!width) {
        return;
      }
      this.dividerHide = width < 390;
    },
    handleTextChange({ quill }) {
      const maxLength = 100;
      if (!quill) return;

      const text = quill.getText();
      const textLength = text.trim().length;

      if (textLength > maxLength) {
        quill.deleteText(maxLength, textLength);
      }
    },
  },
  validations() {
    return {
      limitAmount: {
        minValue: helpers.withMessage('輸入數字需要至少為1', minValue(1)),
        required: requiredIf('限制金額', this.orderInfo.limit === CONSUMPTION_LIMIT.AMOUNT),
      },
      limitItems: {
        minValue: helpers.withMessage('輸入數字需要至少為1', minValue(1)),
        required: requiredIf('限制品項數目', this.orderInfo.limit === CONSUMPTION_LIMIT.ITEMS),
      },
      orderInfo: {
        limit: {
          required: required('消費額度'),
        },
        name: {
          required: required('訂購名稱'),
        },
        startTime: {
          required: required('訂購開始時間'),
        },
        endTime: {
          required: required('訂購結束時間'),
        },
        teamGroupBuyingReceiveTimes: {
          required: required('領取時間'),
        },
        itemStatsReceiver: {
          required: requiredIf('Email', this.orderInfo.enableSendItemStats),
        },
        memberOrdersReceiver: {
          required: requiredIf('Email', this.orderInfo.enableSendMemberOrders),
        },
        orderRedeemsReceiver: {
          required: requiredIf('Email', this.orderInfo.enableSendOrderRedeems),
        },
        subsidy: {
          minValue: helpers.withMessage('請輸入一個數字', minValue(0)),
          required: requiredIf('每人補助金額', this.orderInfo.subsidy !== 0 && !this.loading),
        },
      },
      menuItems: {
        $each: helpers.forEach({
          limit: {
            integer: integer('訂購上限'),
            minValue: helpers.withMessage(`最少輸入：${this.minEntreeLimit}`, minValue(this.minEntreeLimit)),
            maxValue: helpers.withMessage(`最多輸入：${this.maxEntreeLimit}`, maxValue(this.maxEntreeLimit)),
            minLimit: helpers.withMessage(
              '不可小於已訂數量',
              (val, current) => {
                if (!current.limit) {
                  return true;
                }
                return (this.menuItemsLimits[current.index]?.current || 0) <= current.limit;
              },
            ),
          },
        }),
      },
      enableSchedule: {
        crossday: helpers.withMessage('指定領取時段起始日期必需為同一天，才能啟用例行性訂購', (val) => {
          if (val && this.hasSchedule) {
            for (let i = 1; i < this.sortedReceiveTimes.length; i += 1) {
              const { startTime: aStart } = this.sortedReceiveTimes[i - 1];
              const { startTime: bStart } = this.sortedReceiveTimes[i];
              if (!dayjs(aStart).isSame(dayjs(bStart), 'day')) {
                return false;
              }
            }
          }
          return true;
        }),
        orderPeriodTime: helpers.withMessage(
          '開放訂購結束時間不得晚於指定領取時段開始時間',
          () => this.isOrderPeriodEndSameOrBeforeReceiveTimeStart,
        ),
        receiveDate: helpers.withMessage('最後領取日不能小於指定領取時段', (val) => {
          if (val) {
            return !this.isReceiveTimeAfterScheduleReceiveTime;
          }
          return true;
        }),
      },
    };
  },
};
